import { useEffect, useMemo, useRef } from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { IAdornerImageSource } from "../slideshow-preview-container";

export function useAdornerImageCreator() {
	const {
		platform,
		editor: { client, logger },
	} = useAppContext();

	const imageSourceCache = useRef<eXpress.creator.rendering.IAdornerImageSource[]>([]);

	useEffect(
		() => () => {
			// Make sure to dispose all adorner image sources when we are unmounted.
			imageSourceCache.current.forEach((source) => source.dispose());
			imageSourceCache.current.length = 0;
		},
		[],
	);

	// Shared data between rerenders
	return useMemo(() => {
		const adornerOverlayImageLoader = new eXpress.creator.XhrImageLoader(
			client,
			platform.image,
			eXpress.creator.ResourceNecessity.Required,
			eXpress.core.ImageLod.Medium,
			logger,
		);

		const adornerOptimizedInfoLoader = new eXpress.creator.JsonObjectLoader<
			eXpress.core.IOptimizedAdornerInfo,
			eXpress.api.AdornerResourceUrl
		>(client, platform.ajax, logger);

		const dictionary = new eXpress.collections.Dictionary<any[], Promise<IAdornerImageSource>>({
			toStrFunction: (args) => JSON.stringify(args),
		}); // [IAdornerImage]

		return eXpress.utils.pipe(
			(adornerImage: eXpress.core.IAdornerImage) =>
				eXpress.creator.rendering.createAdornerImageSourceAsync(
					adornerImage,
					{
						client,
						resourceResolver: client,
						logger,
						adornerOverlayImageLoader,
						adornerOptimizedInfoLoader,
					},
					{
						loadOptions: {
							cacheRequests: true,
							reuseRequests: true,
						},
					},
				),
			(fn) =>
				async (...args: [eXpress.core.IAdornerImage]) => {
					const source = await fn(...args);

					// Allow the source to be disposed when we unmount
					imageSourceCache.current.push(source);

					const { uri, width, height } = source;

					return {
						uri,
						size: new eXpress.math.Vector(width, height),
					};
				},
			eXpress.utils.withCache({
				dictionary,
			}),
		);
	}, [client, platform, logger]);
}
